import { useEffect, useCallback, useState } from 'react';

/**
 * Hook para gestionar atajos de teclado globales
 * 
 * Atajos disponibles:
 * - Shift + N: Nuevo evento
 * - Shift + T: Nueva tarea
 * - Shift + B: Ir a Biblioteca
 * - Shift + E: Ir a Eventos
 * - Cmd/Ctrl + K: Búsqueda global
 * - Shift + ?: Mostrar ayuda de atajos
 * - Esc: Cerrar modales
 */
export const useKeyboardShortcuts = ({ 
  onNewEvent,
  onNewTask,
  onGlobalSearch,
  onFocusSearch,
  onShowHelp,
  enabled = true
}) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const handleKeyPress = useCallback((event) => {
    // Ignorar si el usuario está escribiendo en un input/textarea
    const isInputActive = 
      event.target.tagName === 'INPUT' || 
      event.target.tagName === 'TEXTAREA' ||
      event.target.isContentEditable;

    // Cmd/Ctrl + K: Búsqueda global (siempre activo)
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      onGlobalSearch?.();
      return;
    }

    // Esc: Cerrar modales/ayuda
    if (event.key === 'Escape') {
      if (isHelpOpen) {
        setIsHelpOpen(false);
        event.preventDefault();
      }
      return;
    }

    // Si está escribiendo en un campo, no procesar más atajos
    if (isInputActive) return;

    // Requiere Shift + tecla para evitar activación accidental
    if (!event.shiftKey) return;

    switch (event.key.toUpperCase()) {
      case 'N':
        event.preventDefault();
        onNewEvent?.();
        break;
      
      case 'T':
        event.preventDefault();
        onNewTask?.();
        break;
      
      case 'B':
        event.preventDefault();
        onFocusSearch?.(); // Reutilizamos para Biblioteca
        break;
      
      case 'E':
        event.preventDefault();
        onNewTask?.(); // Placeholder, se puede conectar a navegación
        break;
      
      case '?':
        event.preventDefault();
        setIsHelpOpen(true);
        onShowHelp?.();
        break;
      
      default:
        break;
    }
  }, [onNewEvent, onNewTask, onGlobalSearch, onFocusSearch, onShowHelp, isHelpOpen]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress, enabled]);

  return {
    isHelpOpen,
    setIsHelpOpen,
    closeHelp: () => setIsHelpOpen(false),
  };
};

export default useKeyboardShortcuts;
